import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { setLoading } from 'Actions/loading';
import { InputIcon, OutputIcon, EditIcon } from 'Shared/Icons';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import FlowEditForm from 'Shared/flows/flows-interface/FlowEditForm';
import SelectFolderModal from 'Shared/flows/folders/SelectFolderModal';
import { useFolderList } from 'Shared/flows/folders/useFolders';
import { getFlowFolderPath } from 'Shared/flows/folders/folderUtils';
import FlowRunFlow from 'Shared/flows/flows-interface/FlowRunFlow';
import FlowExecutionModal from 'Shared/flows/flows-interface/FlowExecutionModal';
import FlowImportFlow from 'Shared/flows/flows-interface/FlowImportFlow';
import { exportFlows, notFoundMessage } from 'Utils/flowExport';
import FlowImportIntoExisting from 'Shared/flows/flows-interface/FlowImportIntoExisting';
import FlowPostParameters from 'Shared/flows/flows-interface/FlowPostParameters';
import { useFlowRun } from 'Shared/flows/flows-interface/useFlowRun';

const FlowTools = ({ flowId, name, description, external_id, timeout, lbh_status, show_in_root, post_parameters, setInitiateVisible, setFinalizationVisible }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [isEditFlow, setIsEditFlow] = useState(false);
    const [isSelectFolder, setIsSelectFolder] = useState(false);
    const [isRunModal, setIsRunModal] = useState(false);
    const [isExportFlow, setIsExportFlow] = useState(false);
    const [includeSubflows, setIncludeSubflows] = useState(false);
    const [isImportFlow, setIsImportFlow] = useState(false);
    const [isPostParams, setIsPostParams] = useState(false);
    const [postParams, setPostParams] = useState(post_parameters);
    const [simpleVersion, setSimpleVersion] = useState(true);
    const { folders, fetchFolders } = useFolderList();
    const {
        runFlow: handleRunFlow, isRunning, setIsRunning, background, setBackground, response, setResponse,
        responseStatus, setResponseStatus, dialogTitle, setDialogTitle, effectiveTimeout,
        fetchBackgroundResult, stopBackgroundFlow, stopSyncFlow,
    } = useFlowRun(flowId, postParams);
    const folderPath = getFlowFolderPath(folders, flowId);

    useEffect(() => {
        setPostParams(post_parameters);
    }, [post_parameters]);

    useEffect(() => {
        if (isExportFlow) {
            exportFlows([flowId], includeSubflows)
                .then(({ flows, not_found: notFound }) => {
                    if (notFound.length) {
                        enqueueSnackbar(notFoundMessage(notFound), { action: CustomSnackbar, variant: 'warning' });
                    }
                    setDialogTitle('Export Flow');
                    setResponse(JSON.stringify(flows.length === 1 ? flows[0] : flows));
                })
                .catch(() => {})
                .finally(() => {
                    dispatch(setLoading(false));
                });
        }
    }, [isExportFlow, flowId, dispatch, includeSubflows, enqueueSnackbar, setDialogTitle, setResponse]);

    const handleImportSuccess = () => {
        // Reload the page to show the updated flow
        window.location.reload();
    };

    return (
        <>
            <FlowEditForm
                flowId={flowId}
                name={name}
                description={description}
                external_id={external_id}
                lbh_status={lbh_status}
                show_in_root={show_in_root}
                visibleEditFlowForm={isEditFlow}
                setVisibleEditFlowForm={setIsEditFlow}
            />
            <SelectFolderModal
                open={isSelectFolder}
                flowId={flowId}
                onClose={() => { setIsSelectFolder(false); fetchFolders(); }}
            />
            <FlowImportFlow
                id={flowId}
                flowId={flowId}
                visible={!!response}
                setVisible={setIsExportFlow}
                response={response}
                setResponse={setResponse}
                name={name}
                title={dialogTitle}
                status={responseStatus}
                setStatus={setResponseStatus}
                simpleVersion={simpleVersion}
                setSimpleVersion={setSimpleVersion}
                includeSubflows={includeSubflows}
                setIncludeSubflows={setIncludeSubflows}
                isBackground={!!background}
                onGetResult={fetchBackgroundResult}
                onBackgroundReset={() => setBackground(null)}
                onStop={stopBackgroundFlow}
            />
            <FlowImportIntoExisting
                flowId={flowId}
                visible={isImportFlow}
                setVisible={setIsImportFlow}
                onImportSuccess={handleImportSuccess}
                onImportAndRun={handleRunFlow}
            />
            <FlowRunFlow id={flowId} visible={isRunModal} setVisible={setIsRunModal} />
            <FlowExecutionModal open={isRunning} timeout={effectiveTimeout} onStop={stopSyncFlow} onClose={() => setIsRunning(false)} />
            <FlowPostParameters
                flowId={flowId}
                visible={isPostParams}
                setVisible={setIsPostParams}
                postParameters={postParams}
                onUpdate={setPostParams}
            />
            <Box
                sx={{
                    display: 'flex',
                    position: 'relative',
                    marginTop: '-10px',
                    marginBottom: '30px',
                    marginLeft: 'auto',
                    paddingBottom: '20px',
                    backgroundColor: 'transparent',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        left: '-35px',
                        right: '-35px',
                        bottom: 0,
                        height: '1px',
                        backgroundColor: '#252D3D',
                    }}
                ></Box>

                <Box>
                    <Button
                        variant="contained"
                        startIcon={<InputIcon />}
                        onClick={() => setInitiateVisible(true)}
                        sx={{
                            marginRight: '20px',
                            color: '#3B465C',
                            backgroundColor: '#F2F7F9',
                            borderRadius: '4px',
                            '&:hover': { backgroundColor: '#DADEE0', boxShadow: 'none' },
                            '&:active': { backgroundColor: '#DADEE0', boxShadow: 'inset 0px 4px 10px rgba(2, 10, 38, 0.45)' },
                        }}
                    >
                        Define Inputs
                    </Button>
                    <Button
                        variant="contained"
                        endIcon={<OutputIcon />}
                        onClick={() => setFinalizationVisible(true)}
                        sx={{
                            color: '#3B465C',
                            backgroundColor: '#F2F7F9',
                            borderRadius: '4px',
                            '&:hover': { backgroundColor: '#DADEE0', boxShadow: 'none' },
                            '&:active': { backgroundColor: '#DADEE0', boxShadow: 'inset 0px 4px 10px rgba(2, 10, 38, 0.45)' },
                        }}
                    >
                        Define Outputs
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setIsSelectFolder(true)}
                        sx={{
                            marginLeft: '20px',
                            color: '#3B465C',
                            backgroundColor: '#F2F7F9',
                            borderRadius: '4px',
                            '&:hover': { backgroundColor: '#DADEE0', boxShadow: 'none' },
                            '&:active': { backgroundColor: '#DADEE0', boxShadow: 'inset 0px 4px 10px rgba(2, 10, 38, 0.45)' },
                        }}
                    >
                        Put to Folder
                    </Button>
                </Box>
                <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Button
                        sx={{
                            color: '#F2F7F9',
                            backgroundColor: '#FF9800',
                            fontWeight: 500,
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            marginRight: '15px',
                            '&:hover': { backgroundColor: '#F57C00', boxShadow: 'none' },
                            '&:active': { backgroundColor: '#F57C00', boxShadow: 'inset 0px 4px 10px rgba(2, 10, 38, 0.45)' },
                        }}
                        onClick={() => setIsImportFlow(true)}
                    >
                        Import Flow
                    </Button>
                    <Button
                        sx={{
                            color: '#3B465C',
                            backgroundColor: '#2FDE8A',
                            fontWeight: 500,
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            marginRight: '15px',
                            '&:hover': { backgroundColor: '#2AC87C', boxShadow: 'none' },
                            '&:active': { backgroundColor: '#2AC87C', boxShadow: 'inset 0px 4px 10px rgba(2, 10, 38, 0.45)' },
                        }}
                        onClick={() => setIsExportFlow(true)}
                    >
                        Export Flow
                    </Button>
                    <Button
                        sx={{
                            color: '#3B465C',
                            backgroundColor: postParams?.query || postParams?.body ? '#90CAF9' : '#F2F7F9',
                            fontWeight: 500,
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            marginRight: '15px',
                            '&:hover': { backgroundColor: '#64B5F6', boxShadow: 'none' },
                            '&:active': { backgroundColor: '#64B5F6', boxShadow: 'inset 0px 4px 10px rgba(2, 10, 38, 0.45)' },
                        }}
                        onClick={() => setIsPostParams(true)}
                    >
                        Parameters
                    </Button>
                    <Button
                        sx={{
                            color: '#3B465C',
                            backgroundColor: '#2FDE8A',
                            fontWeight: 500,
                            borderRadius: '4px',
                            textTransform: 'uppercase',
                            '&:hover': { backgroundColor: '#2AC87C', boxShadow: 'none' },
                            '&:active': { backgroundColor: '#2AC87C', boxShadow: 'inset 0px 4px 10px rgba(2, 10, 38, 0.45)' },
                        }}
                        onClick={handleRunFlow}
                        variant="contained"
                    >
                        Run Flow
                    </Button>
                </Box>
            </Box>
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    marginTop: '-15px',
                    marginBottom: '20px',
                }}
            >
                <Box sx={{width: '80%', display: 'flex', alignItems: 'center'}}>
                    <Box
                        style={{
                            position: 'relative',
                            marginRight: '25px',
                        }}
                    >
                        <IconButton
                            style={{
                                width: '50px',
                                height: '50px',
                                backgroundColor: '#414D65',
                            }}
                            onClick={() => {
                                setIsEditFlow(true);
                            }}
                        >
                            <EditIcon style={{ color: '#F2F7F9' }} />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            position: 'relative',
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {folderPath && (
                            <Typography
                                className="text-grey"
                                sx={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    marginBottom: '4px',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {folderPath}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontWeight: 500,
                                fontSize: '20px',
                                lineHeight: '26px',
                                color: '#F2F7F9',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {name}
                        </Typography>
                        {description && description !== name && (
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    marginTop: '4px',
                                    color: '#F2F7F9',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {description}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Box sx={{width: '20%', display: 'flex', justifyContent: 'flex-end'}}>
                    <Box sx={{ marginBottom: '20px', textAlign: 'right' }}>
                        <Typography
                            sx={{
                                fontSize: '1rem',
                                marginRight: '5px',
                                marginLeft: '5px',
                                color: '#F2F7F9',
                            }}
                        >
                            Timeout, sec: {timeout ? timeout : 'not set'}
                        </Typography>
                        {external_id && (
                            <Typography
                                className="text-grey"
                                sx={{
                                    fontSize: '14px',
                                    marginTop: '4px',
                                    marginRight: '5px',
                                    marginLeft: '5px',
                                }}
                            >
                                {external_id}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default FlowTools;
